import { observer } from "mobx-react-lite";
import { ReactNode, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import LoadingPlaceholder from "../../components/common/LoadingPlaceholder/LoadingPlaceholder";
import { ENV_VARS } from "../../config/env";
import RouterConstants from "../../constants/RouterConstants";
import { getSelectedOrganization, setSelectedOrganization } from "../../helpers/localStorage";
import useOrgNavigation from "../../hooks/useOrgNavigation";
import { useAppStore } from "../../hooks/useStores";
import styles from "./PrivateLayout.module.scss";

interface LayoutProps {
	ignoreUserChecks?: boolean;
	ignoreOrgs?: boolean;
	displayAppsWelcomePopup?: boolean;
}

const PrivateLayout = ({
	ignoreUserChecks = false,
	ignoreOrgs = true,
	// displayAppsWelcomePopup = false,
}: LayoutProps) => {
	const { authStore, appStore, uiStore } = useAppStore();
	// const [showWelcomePopup, setShowWelcomePopup] = useState(false);
	const [showMfaRemovalPopup, setShowMfaRemovalPopup] = useState(false);
	// const [showLeadSourcePopup, setShowLeadSourcePopup] = useState(false);
	const { isRouteRequireOrg } = useOrgNavigation();
	const loc = useLocation();

	// const pathname = window.location.pathname;
	// const isMfaRoute = pathname.includes(RouterConstants.MFA_TOTP_SETUP.path);
	// const isPluginAuthFlow =
	// 	pathname.includes(RouterConstants.PLUGIN_AUTH.path) ||
	// 	pathname.includes(RouterConstants.PLUGIN_LOGIN_STATUS.path);

	// useEffect(() => {
	// 	const loadData = async () => {
	// 		if (authStore.user) {
	// 			const hidePlatformAppsWelcomePopup = await authStore.user?.getFreshSettingsCopy(
	// 				USER_SETTINGS.hidePlatformAppsWelcomePopup,
	// 			);
	// 			setShowWelcomePopup(!hidePlatformAppsWelcomePopup);
	// 		}
	// 	};
	//
	// 	loadData();
	// }, [authStore.user]);

	// useEffect(() => {
	// 	const loadLeadSourcePopupSetting = async () => {
	// 		// Show lead source popup for users who:
	// 		// 1. Were created after the lead source deployment date
	// 		// 2. Are returning users (this is at least their 2nd login)
	// 		// 3. Haven't provided lead source info yet
	// 		// 4. Haven't seen the popup before
	// 		const firebaseUser = firebaseAuth.currentUser;
	//
	// 		if (
	// 			authStore.user &&
	// 			!authStore.isLoading &&
	// 			firebaseUser &&
	// 			isUserCreatedAfterDate(firebaseUser, new Date("2025-09-15")) &&
	// 			authStore.isLoginProcess &&
	// 			!authStore.user.leadSource
	// 		) {
	// 			const hideLeadSourcePopup = await authStore.user?.getFreshSettingsCopy(
	// 				USER_SETTINGS.hideLeadSourcePopup,
	// 			);
	// 			if (!hideLeadSourcePopup) {
	// 				setShowLeadSourcePopup(true);
	// 			}
	// 		}
	// 	};
	//
	// 	loadLeadSourcePopupSetting();
	// }, [authStore.user, authStore.isLoading]);

	useEffect(() => {
		if (
			!authStore.isLoggedIn &&
			authStore.isInitialized &&
			!authStore.isRedirectingToLogout &&
			!authStore.isError
		) {
			const newPlatformUrl = ENV_VARS.VITE_NEW_PLATFORM_URL ?? "";
			if (newPlatformUrl) {
				const returnUrl = encodeURIComponent(window.location.href);
				window.location.href = `${newPlatformUrl}/login?returnUrl=${returnUrl}`;
			}
		}
	}, [authStore.isLoggedIn, authStore.isInitialized, authStore.isRedirectingToLogout, authStore.isError]);

	// useEffect(() => {
	// 	window.addEventListener("storage", async (e) => {
	// 		if (e.key === "selected_org" && e.newValue !== e.oldValue) {
	// 			window.location.reload();
	// 		}
	// 	});
	// }, [getSelectedOrganization()?.organization]);

	const getContent = (): ReactNode => {
		const queryParams = new URLSearchParams(location.search);

		if (authStore.isLoggedIn && isRouteRequireOrg(window.location.pathname)) {
			localStorage.setItem("destination_route", window.location.href);
			uiStore.showDialog("CreateOrganizationDialog");
			return <Navigate to={`${RouterConstants.CONSOLE.fullPath}?${queryParams.toString()}`} state={loc.state} />;
		}

		if (!authStore.isLoggedIn) {
			return <LoadingPlaceholder className={styles.loaderStyle} isLoading />;
		} else {
			/**
			 * We always prioritize the user invitations, we don't need to create an organization of fill the
			 * user info in case of finding a pending invitation (the user info will be filled on the next login)
			 */
			const needsRedirect = !getSelectedOrganization();
			const redirectAllowed =
				!ignoreUserChecks &&
				!authStore?.isError &&
				!(
					appStore.invitedUser &&
					appStore.invitedUser.email?.toLowerCase() === authStore?.user?.email?.toLowerCase()
				);
			if (needsRedirect && redirectAllowed) {
				if (ignoreOrgs && authStore?.userOrganizations.length > 0) {
					setSelectedOrganization(authStore?.userOrganizations[0]);
				}
				// else if (authStore?.userOrganizations?.length === 0 && !ignoreOrgs) {
				// 	return <Navigate to={RouterConstants.CREATE_ORGANIZATION.path} />;
				// } else if (authStore?.userOrganizations?.length > 1 && !ignoreOrgs) {
				// 	return <Navigate to={RouterConstants.SELECT_ORGANIZATION.path} />;
				// }
				else {
					return <Outlet />;
				}
			} else {
				// Check for MFA removal condition
				if (!authStore.isMfaSetupRequired && authStore.isMfaSetupCompleted && !showMfaRemovalPopup) {
					setShowMfaRemovalPopup(true);
				}

				return <Outlet />;
			}
		}
	};

	return (
		<LoadingPlaceholder className={styles.loaderStyle} isLoading={authStore.isLoading || !authStore.isInitialized}>
			{getContent()}
			{/*{!authStore.isLoadingOrgSubscriptions &&*/}
			{/*	!authStore.isPremiumOrgSubscription &&*/}
			{/*	displayAppsWelcomePopup &&*/}
			{/*	!isCampaignEditor && (*/}
			{/*		<AppsWelcomePopup*/}
			{/*			open={showWelcomePopup}*/}
			{/*			onClose={() => {*/}
			{/*				setShowWelcomePopup(false);*/}
			{/*				authStore.user?.updateSettings(USER_SETTINGS.hidePlatformAppsWelcomePopup, true);*/}
			{/*			}}*/}
			{/*		/>*/}
			{/*	)}*/}
			{/*<ApiLimitReachedPopup />*/}
			{/*{!isMfaRoute && !isPluginAuthFlow && !iframeStore.isIframe() ? (*/}
			{/*	<>*/}
			{/*		<FreeToStarterConversionPopup />*/}
			{/*	</>*/}
			{/*) : null}*/}
			{/*<InvalidSubscriptionPopup />*/}
			{/*<MfaRemovalPopup*/}
			{/*	open={showMfaRemovalPopup}*/}
			{/*	onSuccess={() => {*/}
			{/*		setShowMfaRemovalPopup(false);*/}
			{/*	}}*/}
			{/*/>*/}
			{/*<LeadSourcePopup*/}
			{/*	open={showLeadSourcePopup}*/}
			{/*	onClose={() => {*/}
			{/*		setShowLeadSourcePopup(false);*/}
			{/*	}}*/}
			{/*/>*/}
		</LoadingPlaceholder>
	);
};

const ObservedComponent = observer(PrivateLayout);
export default ObservedComponent;
